import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Scan, Book, TrendingUp, Award, BarChart3 } from 'lucide-react';
import { motion } from 'framer-motion';

export const StudentDashboard = () => {
  const [subjects, setSubjects] = useState([]);
  const [progress, setProgress] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [subjectsRes, progressRes] = await Promise.all([
        api.get('/student/subjects'),
        api.get('/analytics/student-progress')
      ]);
      setSubjects(subjectsRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const subjectColors = {
    PHY: 'from-blue-500 to-indigo-500',
    CHEM: 'from-green-500 to-emerald-500',
    BIO: 'from-purple-500 to-pink-500',
    MATH: 'from-orange-500 to-amber-500',
    SCI: 'from-cyan-500 to-teal-500',
  };

  const quizCount = progress?.quiz_attempts?.length || 0;
  const avgScore = quizCount > 0 ? Math.round(progress.quiz_attempts.reduce((s, a) => s + a.score, 0) / quizCount) : 0;
  const modelsViewed = progress?.events?.length || 0;

  return (
    <Layout title="Student Dashboard">
      <div className="space-y-6 sm:space-y-8">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <h1 data-testid="student-dashboard-title" className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-2 sm:mb-4 text-indigo-900">Explore & Learn</h1>
          <p className="text-slate-600 text-sm sm:text-lg">Scan textbook images or browse by subject</p>
        </motion.div>

        {/* Scan Button */}
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}>
          <Button data-testid="scan-textbook-btn" onClick={() => navigate('/student/scan')}
            className="w-full h-20 sm:h-32 rounded-2xl sm:rounded-3xl text-base sm:text-xl font-bold shadow-2xl bg-gradient-to-r from-indigo-600 to-cyan-500 hover:from-indigo-700 hover:to-cyan-600 transition-all duration-300 hover:scale-[1.02] active:scale-95">
            <Scan className="w-6 h-6 sm:w-10 sm:h-10 mr-2 sm:mr-4" /> Scan AR Marker
          </Button>
        </motion.div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3 sm:gap-6">
          <Card className="border-slate-200 shadow-md hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/student/progress')}>
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold text-indigo-900">{quizCount}</p>
                  <p className="text-[10px] sm:text-sm text-slate-600">Quizzes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-md hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/student/progress')}>
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Award className="w-4 h-4 sm:w-6 sm:h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold text-indigo-900">{avgScore}%</p>
                  <p className="text-[10px] sm:text-sm text-slate-600">Avg Score</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-md hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/student/progress')}>
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Book className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold text-indigo-900">{modelsViewed}</p>
                  <p className="text-[10px] sm:text-sm text-slate-600">Viewed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* View Full Progress Link */}
        <div className="text-center">
          <Button data-testid="view-progress-btn" variant="outline" onClick={() => navigate('/student/progress')}
            className="rounded-full border-indigo-200 text-indigo-600 hover:bg-indigo-50 text-xs sm:text-sm">
            <BarChart3 className="w-4 h-4 mr-2" /> View Full Progress & Analytics
          </Button>
        </div>

        {/* Subjects Grid */}
        <div>
          <h2 className="text-lg sm:text-2xl font-bold mb-4 sm:mb-6 text-indigo-900">Your Subjects</h2>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {subjects.map((subject, index) => (
              <motion.div key={subject.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + index * 0.1 }}>
                <Card data-testid={`subject-card-${subject.code}`}
                  className="group cursor-pointer border-slate-200 hover:border-indigo-300 transition-all hover:shadow-xl hover:-translate-y-1 active:scale-95"
                  onClick={() => navigate(`/student/subject/${subject.id}`)}>
                  <CardContent className="p-4 sm:pt-6 sm:p-6">
                    <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${subjectColors[subject.code] || 'from-slate-400 to-slate-600'} mb-3 sm:mb-4 flex items-center justify-center shadow-lg`}>
                      <Book className="w-5 h-5 sm:w-8 sm:h-8 text-white" />
                    </div>
                    <h3 className="text-sm sm:text-xl font-bold mb-1 text-indigo-900 group-hover:text-indigo-600 transition-colors">{subject.name}</h3>
                    <p className="text-[10px] sm:text-sm text-slate-500">Tap to explore</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};
